import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { LandingPage } from '../features/landing/LandingPage';
import { ProtectedRoute } from '../features/auth/ProtectedRoute';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
import { PageSkeleton } from '../components/PageSkeleton';

/**
 * The landing page is eager — it is the first paint for most visitors. The
 * authenticated app, the legal corpus, and the demo are all split out, so a
 * visitor who never logs in never downloads them.
 */
/** Lazily loads a named export, preserving its component type. */
function lz<K extends string, M extends Record<K, ComponentType>>(
  loader: () => Promise<M>,
  key: K,
) {
  return lazy(() => loader().then((module) => ({ default: module[key] })));
}

const LoginPage = lz(() => import('../features/auth/LoginPage'), 'LoginPage');
const DemoPage = lz(() => import('../features/demo/DemoPage'), 'DemoPage');
const LegalPage = lz(() => import('../features/legal/LegalPage'), 'LegalPage');
const NotFoundPage = lz(() => import('../features/misc/NotFoundPage'), 'NotFoundPage');

const AppShell = lz(() => import('../components/AppShell'), 'AppShell');
const AppHome = lz(() => import('../components/AppShell'), 'AppHome');
const CounterPage = lz(() => import('../features/counter/CounterPage'), 'CounterPage');
const CatalogListPage = lz(() => import('../features/catalog/CatalogListPage'), 'CatalogListPage');
const CatalogDetailPage = lz(() => import('../features/catalog/CatalogDetailPage'), 'CatalogDetailPage');
const InventoryPage = lz(() => import('../features/inventory/InventoryPage'), 'InventoryPage');
const CatalogFormPage = lz(() => import('../features/catalog/CatalogFormPage'), 'CatalogFormPage');
const MovePage = lz(() => import('../features/inventory/MovePage'), 'MovePage');
const CountListPage = lz(() => import('../features/counting/CountListPage'), 'CountListPage');
const CountSheetPage = lz(() => import('../features/counting/CountSheetPage'), 'CountSheetPage');
const InboxPage = lz(() => import('../features/inbox/InboxPage'), 'InboxPage');
const JobListPage = lz(() => import('../features/jobs/JobListPage'), 'JobListPage');
const JobDetailPage = lz(() => import('../features/jobs/JobDetailPage'), 'JobDetailPage');
const RequisitionQueuePage = lz(() => import('../features/jobs/RequisitionQueuePage'), 'RequisitionQueuePage');
const BillingQueuePage = lz(() => import('../features/jobs/BillingQueuePage'), 'BillingQueuePage');
const CustomersPage = lz(() => import('../features/customers/CustomersPage'), 'CustomersPage');
const CustomerDetailPage = lz(() => import('../features/customers/CustomerDetailPage'), 'CustomerDetailPage');
const SettingsPage = lz(() => import('../features/settings/SettingsPage'), 'SettingsPage');
const ReviewPage = lz(() => import('../features/inbox/ReviewPage'), 'ReviewPage');
const ProfilePage = lz(() => import('../features/settings/ProfilePage'), 'ProfilePage');
const NotificationsPage = lz(() => import('../features/notifications/NotificationsPage'), 'NotificationsPage');

// The platform console: HXIQ's own operators, a separate session from any
// shop's (ADR-0011). Split out like the app, since almost nobody is one.
const PlatformRoot = lz(() => import('../features/platform/PlatformLayout'), 'PlatformRoot');
const PlatformShell = lz(() => import('../features/platform/PlatformLayout'), 'PlatformShell');
const PlatformLoginPage = lz(() => import('../features/platform/PlatformLoginPage'), 'PlatformLoginPage');
const PlatformTenantsPage = lz(() => import('../features/platform/PlatformTenantsPage'), 'PlatformTenantsPage');
const PlatformTenantDetailPage = lz(() => import('../features/platform/PlatformTenantDetailPage'), 'PlatformTenantDetailPage');

// The customer portal: a shop's customer, signed in as one of its contacts
// (ADR-0013). Its own session, apart from the shop's and the console's.
const PortalRoot = lz(() => import('../features/portal/PortalLayout'), 'PortalRoot');
const PortalShell = lz(() => import('../features/portal/PortalLayout'), 'PortalShell');
const PortalLoginPage = lz(() => import('../features/portal/PortalLoginPage'), 'PortalLoginPage');
const PortalInvitePage = lz(() => import('../features/portal/PortalInvitePage'), 'PortalInvitePage');
const PortalHomePage = lz(() => import('../features/portal/PortalHomePage'), 'PortalHomePage');
const PortalEquipmentPage = lz(() => import('../features/portal/PortalEquipmentPage'), 'PortalEquipmentPage');
const PortalUnitPage = lz(() => import('../features/portal/PortalEquipmentPage'), 'PortalUnitPage');
const PortalVisitPage = lz(() => import('../features/portal/PortalVisitPage'), 'PortalVisitPage');
const PortalQuotePage = lz(() => import('../features/portal/PortalQuotePage'), 'PortalQuotePage');
const PortalOrderPage = lz(() => import('../features/portal/PortalOrderPage'), 'PortalOrderPage');

// A page-shaped skeleton while a chunk loads: loading is never mistaken for
// empty, and the layout does not jump when the page lands.
const s = (element: ReactNode) => <Suspense fallback={<PageSkeleton />}>{element}</Suspense>;

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // The home page is the login screen. The marketing page was a Phase 1
      // artifact for a product that has since become a shop's daily tool —
      // whoever lands here works here. The old page stays reachable at /about.
      { path: '/', element: s(<LoginPage />) },
      { path: '/login', element: <Navigate to="/" replace /> },
      { path: '/about', element: <LandingPage /> },
      { path: '/demo', element: s(<DemoPage />) },
      { path: '/legal/:slug', element: s(<LegalPage />) },
      { path: '*', element: s(<NotFoundPage />) },
    ],
  },
  {
    path: '/platform',
    element: s(<PlatformRoot />),
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: 'login', element: s(<PlatformLoginPage />) },
      {
        element: s(<PlatformShell />),
        children: [
          { index: true, element: <Navigate to="/platform/tenants" replace /> },
          { path: 'tenants', element: s(<PlatformTenantsPage />) },
          { path: 'tenants/:id', element: s(<PlatformTenantDetailPage />) },
        ],
      },
    ],
  },
  {
    path: '/portal',
    element: s(<PortalRoot />),
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: 'login', element: s(<PortalLoginPage />) },
      { path: 'invite/:token', element: s(<PortalInvitePage />) },
      {
        element: s(<PortalShell />),
        children: [
          { index: true, element: s(<PortalHomePage />) },
          { path: 'equipment', element: s(<PortalEquipmentPage />) },
          { path: 'equipment/:id', element: s(<PortalUnitPage />) },
          { path: 'visits/:id', element: s(<PortalVisitPage />) },
          { path: 'quotes/:id', element: s(<PortalQuotePage />) },
          { path: 'orders/:id', element: s(<PortalOrderPage />) },
        ],
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/app',
        element: s(<AppShell />),
        errorElement: <RouteErrorBoundary />,
        children: [
          // The first item on the person's own bar — Jobs for almost everyone,
          // the counter for a clerk with no job access. See AppHome.
          { index: true, element: s(<AppHome />) },
          { path: 'counter', element: s(<CounterPage />) },
          { path: 'catalog', element: s(<CatalogListPage />) },
          // `new` must precede `:id` or it is captured as an id.
          { path: 'catalog/new', element: s(<CatalogFormPage />) },
          { path: 'catalog/:id', element: s(<CatalogDetailPage />) },
          { path: 'catalog/:id/edit', element: s(<CatalogFormPage />) },
          { path: 'inventory', element: s(<InventoryPage />) },
          { path: 'inventory/move', element: s(<MovePage />) },
          { path: 'counts', element: s(<CountListPage />) },
          { path: 'counts/:id', element: s(<CountSheetPage />) },
          { path: 'jobs', element: s(<JobListPage />) },
          { path: 'jobs/:id', element: s(<JobDetailPage />) },
          { path: 'requisitions', element: s(<RequisitionQueuePage />) },
          { path: 'billing', element: s(<BillingQueuePage />) },
          { path: 'customers', element: s(<CustomersPage />) },
          { path: 'customers/:id', element: s(<CustomerDetailPage />) },
          { path: 'settings', element: s(<SettingsPage />) },
          { path: 'profile', element: s(<ProfilePage />) },
          { path: 'notifications', element: s(<NotificationsPage />) },
          { path: 'inbox', element: s(<InboxPage />) },
          { path: 'inbox/:id', element: s(<ReviewPage />) },
        ],
      },
    ],
  },
]);
